import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, MenuItem, Paper,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import PageLayout from '../shell/PageLayout';
import { request } from './OperationsProvider';
import useOperationalPermissions from '../common/util/operationalPermissions';
import {
  insightsDate, insightsRange, km, percent, hours,
} from './fleetInsights';
import DownloadTable from '../shell/DownloadTable';

const Metric = ({ label, value, note }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: '1 1 170px' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ my: 0.5 }}>{value}</Typography>
    <Typography variant="caption" color="text.secondary">{note}</Typography>
  </Paper>
);
const FleetInsightsPage = () => {
  const permissions = useOperationalPermissions();
  const [range, setRange] = useState(() => insightsRange(30));
  const [group, setGroup] = useState('week'); const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false); const [error, setError] = useState('');
  const [vehicle, setVehicle] = useState(null); const [cargo, setCargo] = useState('LOADED'); const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false); const [saveError, setSaveError] = useState('');
  const load = useCallback(async (signal) => {
    setLoading(true); setError('');
    try {
      const query = new URLSearchParams({ ...range, group });
      const result = await request(`/api/operations/fleet-insights?${query}`, 'GET', null, signal);
      if (!signal?.aborted) setData(result);
    } catch (e) { if (e.name !== 'AbortError') setError(e.message); } finally { if (!signal?.aborted) setLoading(false); }
  }, [range, group]);
  useEffect(() => {
    const controller = new AbortController(); load(controller.signal);
    return () => controller.abort();
  }, [load]);
  const save = async () => {
    setSaving(true); setSaveError('');
    try { await request(`/api/operations/vehicles/${vehicle.vehicleId}/cargo`, 'POST', { state: cargo, note }); setVehicle(null); await load(); } catch (e) { setSaveError(e.message); } finally { setSaving(false); }
  };
  const summary = data?.summary;
  const speed = (row) => (row.speedFixes > 0 || row.overspeedEvents > 0 ? row.overspeedEvents : 'Unavailable');
  const braking = (row) => (row.brakingAvailable ? row.harshBrakingEvents : 'Not reported');
  // Each signal answers for itself. A unit that sends braking and not cornering is ordinary,
  // and showing a nil where the hardware is silent reads as a clean record rather than a gap.
  const accelerating = (row) => (row.accelerationAvailable ? row.harshAccelerationEvents : 'Not reported');
  const cornering = (row) => (row.corneringAvailable ? row.harshCorneringEvents : 'Not reported');
  const preset = (days) => setRange(insightsRange(days));
  const title = permissions.haulier ? 'My fleet insights' : 'Fleet insights';
  return (
    <PageLayout title={title} subtitle="Distance, cargo utilisation and driving safety for the vehicles you can access.">
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        <TextField type="date" label="From" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} InputLabelProps={{ shrink: true }} />
        <TextField type="date" label="To" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} InputLabelProps={{ shrink: true }} />
        <TextField select label="Group by" value={group} onChange={(e) => setGroup(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="week">Weekly</MenuItem>
          <MenuItem value="month">Monthly</MenuItem>
        </TextField>
        <Button onClick={() => preset(7)}>Last 7 days</Button>
        <Button onClick={() => preset(30)}>Last 30 days</Button>
        <Button onClick={() => preset(90)}>Last 90 days</Button>
        <Button variant="outlined" onClick={() => load()} disabled={loading}>Refresh</Button>
      </Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        {' '}
        {data && 'Previously loaded figures are shown below.'}
      </Alert>
      )}
      {summary && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Reporting period:
            {insightsDate(data.from)}
            –
            {insightsDate(data.to)}
            {' '}
            · India time · Completed days only; today is not finalised.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            <Metric label="Distance travelled" value={`${km(summary.metres)} km`} note={`${km(summary.loadedMetres)} loaded · ${km(summary.emptyMetres)} empty · ${km(summary.unknownMetres)} unknown`} />
            <Metric label="Loaded-distance utilisation" value={percent(summary.loadedPercent)} note="Loaded kilometres ÷ all observed kilometres" />
            <Metric label="Observed active days" value={percent(summary.activePercent)} note={`${summary.activeDays} vehicle-days moving / ${summary.availableDays} calendar vehicle-days; maintenance is not excluded`} />
            <Metric label="Overspeed episodes" value={speed(summary)} note={`${hours(summary.overspeedMs)} observed hours above configured limits`} />
            <Metric label="Harsh braking" value={braking(summary)} note="Device-reported alarms; sparse GPS cannot establish absence" />
            <Metric label="Harsh acceleration" value={accelerating(summary)} note="Device-reported alarms; not every tracker sends them" />
            <Metric label="Harsh cornering" value={cornering(summary)} note="Device-reported alarms; not every tracker sends them" />
            <Metric label="Completed deliveries" value={summary.completedTrips} note={`${summary.tripsPerActiveDay ?? 'Unavailable'} per observed active vehicle-day; confirmed completion only`} />
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            {data.note}
            {' '}
            Observed interval coverage:
            {' '}
            {percent(summary.observedPercent)}
            {' '}
            of calendar vehicle-hours. No telemetry is not evidence of inactivity.
          </Alert>
          {summary.pendingDays > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {summary.pendingDays}
            {' '}
            daily records are awaiting enrichment. Safety and cargo figures are partial; refresh as history is processed.
          </Alert>
          )}
          {data.history?.some((h) => h.error) && <Alert severity="warning" sx={{ mb: 2 }}>Tracking history could not be retrieved for some devices. Check the tracking connection and history permissions; figures may be incomplete.</Alert>}
          {summary.fixes === 0 && <Alert severity="warning" sx={{ mb: 2 }}>No usable position samples in this period. Connect reporting devices or wait for retained history to synchronise.</Alert>}
          <Typography variant="h6" sx={{ mb: 1 }}>
            {group === 'week' ? 'Weekly' : 'Monthly'}
            {' '}
            utilisation
          </Typography>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <DownloadTable
              name="fleet-utilisation-by-period"
              rows={data.periods}
              columns={[
                ['Period', (r) => `${insightsDate(r.from)}\u2013${insightsDate(r.to)}`],
                ['Total km', (r) => km(r.metres)],
                ['Loaded km', (r) => km(r.loadedMetres)],
                ['Empty km', (r) => km(r.emptyMetres)],
                ['Unknown km', (r) => km(r.unknownMetres)],
                ['Loaded %', (r) => percent(r.loadedPercent)],
                ['Active days %', (r) => percent(r.activePercent)],
                ['Driving h', (r) => hours(r.movingMs)],
                ['Stationary h', (r) => hours(r.idleMs)],
                ['Overspeed', (r) => speed(r)],
                ['Braking', (r) => braking(r)],
                ['Acceleration', (r) => accelerating(r)],
                ['Cornering', (r) => cornering(r)],
                ['Deliveries', (r) => r.completedTrips],
              ]}
            />
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small" aria-label="Fleet utilisation by period">
              <TableHead>
                <TableRow>
                  {['Period', 'Total km', 'Loaded km', 'Empty km', 'Unknown km', 'Loaded %', 'Active days %', 'Driving h', 'Stationary h', 'Overspeed', 'Braking', 'Deliveries'].map((h) => <TableCell key={h}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.periods.map((r) => (
                  <TableRow key={r.period}>
                    <TableCell>
                      {insightsDate(r.from)}
                      –
                      {insightsDate(r.to)}
                    </TableCell>
                    {[['total', km(r.metres)], ['loaded', km(r.loadedMetres)], ['empty', km(r.emptyMetres)], ['unknown', km(r.unknownMetres)], ['loadedPercent', percent(r.loadedPercent)], ['activePercent', percent(r.activePercent)], ['driving', hours(r.movingMs)], ['stationary', hours(r.idleMs)], ['speed', speed(r)], ['braking', braking(r)], ['acceleration', accelerating(r)], ['cornering', cornering(r)], ['deliveries', r.completedTrips]].map(([key, value]) => <TableCell key={key}>{value}</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6">Vehicles</Typography>
            <DownloadTable
              name="fleet-vehicles"
              rows={data.vehicles}
              columns={[
                ['Vehicle', (r) => r.vehicle],
                ['Current cargo', (r) => r.cargoState || ''],
                ['Total km', (r) => km(r.metres)],
                ['Loaded km', (r) => km(r.loadedMetres)],
                ['Empty km', (r) => km(r.emptyMetres)],
                ['Unknown km', (r) => km(r.unknownMetres)],
                ['Loaded %', (r) => percent(r.loadedPercent)],
                ['Active days', (r) => r.activeDays],
                ['Overspeed', (r) => speed(r)],
                ['Braking', (r) => braking(r)],
                ['Acceleration', (r) => accelerating(r)],
                ['Cornering', (r) => cornering(r)],
                ['Overspeed / 100 km', (r) => r.eventsPer100Km ?? ''],
              ]}
            />
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small" aria-label="Vehicle distance and safety">
              <TableHead>
                <TableRow>
                  {['Vehicle', 'Current cargo', 'Total km', 'Loaded / empty / unknown km', 'Loaded %', 'Active days', 'Overspeed', 'Braking', 'Acceleration', 'Cornering', 'Overspeed / 100 km', ''].map((h) => <TableCell key={h}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.vehicles.map((r) => (
                  <TableRow key={r.vehicleId}>
                    <TableCell>{r.vehicle}</TableCell>
                    <TableCell><Chip size="small" label={r.cargoState.toLowerCase()} title={r.cargoBasis} /></TableCell>
                    <TableCell>{km(r.metres)}</TableCell>
                    <TableCell>
                      {km(r.loadedMetres)}
                      {' '}
                      /
                      {' '}
                      {km(r.emptyMetres)}
                      {' '}
                      /
                      {' '}
                      {km(r.unknownMetres)}
                    </TableCell>
                    <TableCell>{percent(r.loadedPercent)}</TableCell>
                    <TableCell>{r.activeDays}</TableCell>
                    <TableCell>{speed(r)}</TableCell>
                    <TableCell>{braking(r)}</TableCell>
                    <TableCell>{accelerating(r)}</TableCell>
                    <TableCell>{cornering(r)}</TableCell>
                    <TableCell>{r.eventsPer100Km ?? '—'}</TableCell>
                    <TableCell>{(permissions.operate || permissions.haulier) && <Button size="small" onClick={() => { setVehicle(r); setCargo(r.cargoState === 'UNKNOWN' ? 'LOADED' : r.cargoState); setNote(''); setSaveError(''); }}>Record cargo</Button>}</TableCell>
                  </TableRow>
                ))}
                {data.vehicles.length === 0 && <TableRow><TableCell colSpan={10}>No vehicles assigned to your account.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="h6" sx={{ mb: 1 }}>Observed safety events</Typography>
          <Typography variant="caption" color="text.secondary">
            Episode start speed is shown. Braking severity and duration require device support.
            {data.eventCount > 200 && `Showing the latest 200 of ${data.eventCount} events.`}
          </Typography>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <DownloadTable
              name="driving-safety-events"
              rows={data.events}
              columns={[
                ['Time', (e) => new Date(e.at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })],
                ['Vehicle', (e) => e.vehicle],
                ['Driver', (e) => e.driver || ''],
                ['Event', (e) => ({ OVERSPEED: 'Overspeeding', HARSH_BRAKING: 'Harsh braking', HARSH_ACCELERATION: 'Harsh acceleration', HARSH_CORNERING: 'Harsh cornering' }[e.type] || e.type)],
                ['Speed km/h', (e) => e.kph],
                ['Limit km/h', (e) => e.limitKph ?? ''],
                ['Trip', (e) => e.tripId ?? ''],
                ['Latitude', (e) => e.latitude ?? ''],
                ['Longitude', (e) => e.longitude ?? ''],
              ]}
            />
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="Driving safety events">
              <TableHead>
                <TableRow>
                  {['Time', 'Vehicle', 'Driver', 'Event', 'Speed / limit', 'Trip', 'Location'].map((h) => <TableCell key={h}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.events.map((e) => (
                  <TableRow key={`${e.vehicleId}-${e.sourceId}-${e.at}-${e.type}`}>
                    <TableCell>{new Date(e.at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</TableCell>
                    <TableCell>{e.vehicle}</TableCell>
                    <TableCell>{e.driver}</TableCell>
                    <TableCell>{e.type === 'OVERSPEED' ? 'Overspeeding' : 'Harsh braking'}</TableCell>
                    <TableCell>
                      {e.kph}
                      {' '}
                      /
                      {' '}
                      {e.limitKph ?? '—'}
                      {' '}
                      km/h
                    </TableCell>
                    <TableCell>{e.tripId ?? '—'}</TableCell>
                    <TableCell><Button size="small" component="a" href={`https://www.google.com/maps/search/?api=1&query=${Number(e.latitude)},${Number(e.longitude)}`} target="_blank" rel="noopener noreferrer">Map</Button></TableCell>
                  </TableRow>
                ))}
                {data.events.length === 0 && <TableRow><TableCell colSpan={7}>No safety events observed in available samples.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <Dialog open={!!vehicle} onClose={() => { if (!saving) setVehicle(null); }} fullWidth maxWidth="xs">
        <DialogTitle>
          Record cargo —
          {vehicle?.vehicle}
        </DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error">{saveError}</Alert>}
          <Typography variant="body2" sx={{ mb: 2 }}>Applies from now. Your name and reason are recorded; past mileage is unchanged.</Typography>
          <TextField select fullWidth label="Cargo state" value={cargo} onChange={(e) => setCargo(e.target.value)} sx={{ mt: 1 }}>
            <MenuItem value="LOADED">Loaded</MenuItem>
            <MenuItem value="EMPTY">Empty</MenuItem>
            <MenuItem value="UNKNOWN">Unknown</MenuItem>
          </TextField>
          <TextField fullWidth multiline label="Reason / confirmation" value={note} onChange={(e) => setNote(e.target.value)} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setVehicle(null)}>Cancel</Button>
          <Button variant="contained" disabled={saving || !note.trim()} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};
export default FleetInsightsPage;
